<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center justify-between">
            <h2 class="text-lg leading-7 font-medium text-white">
              เพิ่มลูกค้าใหม่
            </h2>
            <button
              @click="onClose"
              aria-label="Close panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div>
            <p class="text-sm leading-5 text-cyan-300">
              เพิ่มลูกค้าเพื่อใช้ตั้งค่าการใช้งานเริ่มต้นให้กับลูกค้า
            </p>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <div class="p-4 space-y-6">
          <div>
            <ValidationProvider
              name="รหัสบริษัทลูกค้า"
              rules="required"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="customer.company_code"
                label="รหัสบริษัทลูกค้า"
                required
                type="text"
                placeholder="ตัวอย่าง : MAS"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <ValidationProvider
              name="ชื่อบริษัทลูกค้า (ไทย)"
              rules="required"
              v-slot="{ errors }"
            >
              <AppInput
                v-model="customer.title_th"
                label="ชื่อบริษัทลูกค้า (ไทย)"
                required
                type="text"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <ValidationProvider
              name="ชื่อบริษัทลูกค้า (อังกฤษ)"
              rules="required"
              v-slot="{ errors }"
            >
              <AppInput
                v-model="customer.title_en"
                label="ชื่อบริษัทลูกค้า (อังกฤษ)"
                required
                type="text"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <AppTextarea v-model="customer.address_th" label="ที่อยู่ (ไทย)" />
          </div>

          <div>
            <AppTextarea
              v-model="customer.address_en"
              label="ที่อยู่ (อังกฤษ)"
            />
          </div>

          <div>
            <AppInput
              v-model="customer.www"
              label="เว็บไซต์"
              type="text"
              placeholder="https://domain.co.th"
            />
          </div>

          <div>
            <AppTextarea v-model="customer.note" label="ใส่โน็ตเพิ่มเติม" />
          </div>

          <div class="sr-only">{{ isDataChange }}</div>
        </div>
      </div>

      <div>
        <div class="p-4 border-t space-x-4 flex justify-end">
          <button
            @click="onCancel"
            :disabled="loading"
            :class="{ 'cursor-not-allowed': loading }"
            type="button"
            class="
              w-full
              inline-flex
              justify-center
              rounded-md
              border border-gray-300
              shadow-sm
              px-4
              py-2
              bg-white
              text-base
              font-medium
              text-gray-700
              hover:bg-gray-50
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
              sm:ml-3 sm:w-auto
            "
          >
            ยกเลิก
          </button>
          <button
            @click="handleSubmit(onSubmit)"
            :disabled="loading"
            :class="{ 'cursor-not-allowed': loading }"
            type="button"
            class="
              w-full
              inline-flex
              justify-center
              rounded-md
              border border-transparent
              shadow-sm
              px-4
              py-2
              bg-cyan-600
              text-base
              font-medium
              text-white
              hover:bg-cyan-700
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-cyan-500
              sm:w-auto
            "
          >
            <svg
              v-if="loading"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            ตกลง
          </button>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import AppInput from '@/components/AppInput'
import AppTextarea from '@/components/AppTextarea'

import { extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})

export default {
  name: 'FormCustomer',

  props: {
    currentCustomer: {
      type: Object,
    },
    customer: {
      type: Object,
    },
  },

  components: {
    AppInput,
    AppTextarea,
  },

  data() {
    return {
      loading: false,
      errors: [],
    }
  },

  computed: {
    isDataChange() {
      if (
        this.customer.company_code !== this.currentCustomer.company_code ||
        this.customer.title_th !== this.currentCustomer.title_th ||
        this.customer.title_en !== this.currentCustomer.title_en ||
        this.customer.address_th !== this.currentCustomer.address_th ||
        this.customer.address_en !== this.currentCustomer.address_en ||
        this.customer.www !== this.currentCustomer.www
      ) {
        this.$emit('isDataChange', true)
        return true
      }
      this.$emit('isDataChange', false)
      return false
    },
  },

  methods: {
    onCancel() {
      this.$emit('onCancel')
    },

    onClose() {
      this.$emit('onClose')
    },

    async onSubmit() {
      try {
        const newCustomer = {
          company_code: this.customer.company_code,
          title_th: this.customer.title_th,
          title_en: this.customer.title_en,
          address_th: this.customer.address_th || null,
          address_en: this.customer.address_en || null,
          www: this.customer.www || null,
          note: this.customer.note || null,
        }

        this.loading = true

        await this.$store.dispatch('customer/addCustomer', newCustomer)

        const notification = {
          type: 'success',
          message: 'Your customer has been added!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
